import { useEffect, useMemo, useState } from 'react';
import { membersAppSelectors } from 'redux/slices/memberSlice';
import { FilterChips, FlexContainer } from '@act/storybook';
import { DEFAULT_ROWS_PER_PAGE_OPTIONS as rowsPerPageOptions } from 'toolkit/tables/standard-table';
import {
  PaginationBounds,
  SortParameters,
} from 'toolkit/tables/types/table.types';
import { DocketNeedsColumnKeys } from './utilities/docketNeedsColumnKeys.type';
import { useApiCall } from 'redux/utils/api-hook';
import { needRequests } from 'redux/requests/need.requests';
import { needsSelectors } from 'redux/slices/needs.slice';
import { useToast } from '@act/react-components/app/ReusableToast';
import { needIsExpiringSoon } from '@act/storybook';
import { useMobile } from '@act/utilities/mobile-context';
import DocketNeedsTableView, {
  NeedDtoWithMember,
} from './DocketNeedsTableView';
import DocketNeedsMobileView from './DocketNeedsMobileView';

type Filter = 'expiring' | 'needs-action';
const filterChoices: { label: string; value: Filter }[] = [
  { label: 'Expiring', value: 'expiring' },
  { label: 'Needs Action', value: 'needs-action' },
];

export default function DocketNeeds() {
  const toast = useToast();
  const { isMobile } = useMobile();
  const [filters, setFilters] = useState<Filter[]>([]);
  const [sortParams, setSortParams] =
    useState<SortParameters<DocketNeedsColumnKeys>>();
  const memberMap = membersAppSelectors.useSelectMembersMap();
  const [_needs, loadingNeeds, _getNeeds] = useApiCall(
    needRequests.getExpiringAndNeedsActionUserNeedsByGuide
  );
  const [patchNeedResult, isPatchingNeed, patchNeed] = useApiCall(
    needRequests.patchNeed
  );
  const loadedNeed =
    needsSelectors.useSelectExpiringAndNeedsActionUserNeedsForGuide();

  const [bounds, setBounds] = useState<PaginationBounds>(() => {
    return {
      pageSize: rowsPerPageOptions[0],
      pageStart: 0,
    };
  });

  useEffect(() => {
    if (patchNeedResult && !isPatchingNeed) {
      toast('Time window extended successfully');
    }
  }, [patchNeedResult, isPatchingNeed]);

  const needsWithMembers: NeedDtoWithMember[] = useMemo(() => {
    if (!loadedNeed || !memberMap) return [];

    return Object.values(loadedNeed).map(need => ({
      ...need,
      member: memberMap[need.memberId],
    }));
  }, [loadedNeed, memberMap]);

  const filteredNeeds = useMemo(() => {
    if (filters.length === 0) return needsWithMembers;

    return needsWithMembers.filter(need => {
      if (filters.includes('expiring')) {
        if (needIsExpiringSoon(need)) return true;
      }

      if (filters.includes('needs-action')) {
        if (!needIsExpiringSoon(need)) return true;
      }

      return false;
    });
  }, [needsWithMembers, filters]);

  const { paginatedNeeds, filteredCount } = useMemo(() => {
    let sortedNeeds = [...filteredNeeds];

    if (sortParams) {
      sortedNeeds.sort((a: NeedDtoWithMember, b: NeedDtoWithMember) => {
        let aValue: string | number;
        let bValue: string | number;

        switch (sortParams.orderBy) {
          case 'member':
            aValue = a.member?.firstName || '';
            bValue = b.member?.firstName || '';
            break;
          case 'issueLabel':
            aValue = a.issueId || '';
            bValue = b.issueId || '';
            break;
          case 'task':
            aValue = needIsExpiringSoon(a) ? 3 : !needIsExpiringSoon(b) ? 2 : 1;
            bValue = needIsExpiringSoon(b) ? 3 : !needIsExpiringSoon(a) ? 2 : 1;
            break;
          case 'dueDate':
            aValue = a.expiresAt || 0;
            bValue = b.expiresAt || 0;
            break;
          default:
            return 0;
        }

        if (sortParams.orderDirection === 'desc') {
          return aValue > bValue ? -1 : aValue < bValue ? 1 : 0;
        } else {
          return aValue < bValue ? -1 : aValue > bValue ? 1 : 0;
        }
      });

      const startIndex = sortParams.pageStart * sortParams.pageSize;
      sortedNeeds = sortedNeeds.slice(
        startIndex,
        startIndex + sortParams.pageSize
      );
    }

    return {
      paginatedNeeds: sortedNeeds,
      filteredCount: filteredNeeds.length,
    };
  }, [filteredNeeds, sortParams]);

  const handleExtendTimeWindow = (needId: number) => {
    patchNeed({
      body: { shouldExtend: true },
      params: { needId },
    });
  };

  return (
    <FlexContainer
      direction="column"
      padding={isMobile ? '16px' : '16px 16px 0'}
      height={isMobile ? 'auto' : '100%'}
      width="100%"
      sx={{
        width: '100%',
        maxWidth: '100%',
        boxSizing: 'border-box',
        overflowX: 'hidden',
        overflowY: 'visible',
      }}
    >
      <FilterChips
        filterChoices={filterChoices}
        setSelectedFilters={setFilters}
      />

      {isMobile ? (
        <DocketNeedsMobileView
          needs={paginatedNeeds}
          totalItems={filteredCount}
          bounds={bounds}
          setBounds={setBounds}
          onExtendTimeWindow={handleExtendTimeWindow}
        />
      ) : (
        <DocketNeedsTableView
          needs={paginatedNeeds}
          totalRecords={filteredCount}
          bounds={bounds}
          setBounds={setBounds}
          handleTableChange={setSortParams}
          onExtendTimeWindow={handleExtendTimeWindow}
          isLoading={loadingNeeds}
        />
      )}
    </FlexContainer>
  );
}
